import { useEffect, useMemo, useState } from 'react';
import Spinner from '../../components/Spinner/Spinner';
import usePenetrationReportV2Store from '../../stores/penetrationReportV2Store';
import {
  fetchMarketRecords, fetchTrendSeries, computeHeroMetrics, computeSignalTiles, aggregateBreakdown,
  filterMarketRecords,
} from '../../services/penetrationReportV2/penetrationReportV2Service';
import { SEED_BRANDS } from '../../services/penetrationReportV2/seedData';
import { MarketRecord, TrendPoint } from '../../services/penetrationReportV2/types';
import { useFilteredMarketRecords } from './useFilteredMarketRecords';
import FilterBar from './FilterBar';
import ComparisonBar from './ComparisonBar';
import HeroCard from './HeroCard';
import TrendChart from './TrendChart';
import SignalTiles from './SignalTiles';
import BreakdownTable from './BreakdownTable';
import FootprintMap from './FootprintMap';
import './PenetrationReportV2.css';

function PenetrationReportV2() {
  const [allRecords, setAllRecords] = useState<MarketRecord[]>([]);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);

  const comparisonEnabled = usePenetrationReportV2Store((s) => s.comparisonEnabled);
  const asOfPeriod = usePenetrationReportV2Store((s) => s.asOfPeriod);
  const comparePeriod = usePenetrationReportV2Store((s) => s.comparePeriod);
  const trendRangeMonths = usePenetrationReportV2Store((s) => s.trendRangeMonths);
  const breakdownTab = usePenetrationReportV2Store((s) => s.breakdownTab);

  useEffect(() => {
    fetchMarketRecords()
      .then(setAllRecords)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchTrendSeries(asOfPeriod, comparePeriod, trendRangeMonths).then(setTrend);
  }, [asOfPeriod, comparePeriod, trendRangeMonths]);

  const filters = usePenetrationReportV2Store((s) => s.filters);
  const filteredRecords = useFilteredMarketRecords(allRecords);

  // Signal tiles must show their own stats off the brand/channel/solution filters
  // only — never off the currently *selected* tile's own narrowed set, or a
  // tile's displayed percentage would shift the moment you select it.
  const recordsForTiles = useMemo(
    () => filterMarketRecords(allRecords, filters, null),
    [allRecords, filters],
  );

  const heroMetrics = useMemo(() => computeHeroMetrics(filteredRecords), [filteredRecords]);
  const signalTiles = useMemo(() => computeSignalTiles(recordsForTiles), [recordsForTiles]);
  const breakdownRows = useMemo(
    () => aggregateBreakdown(filteredRecords, breakdownTab),
    [filteredRecords, breakdownTab],
  );

  return (
    <div className="pr2-page">
      <header className="pr2-topbar">
        <div>
          <p className="pr2-eyebrow">Market intelligence</p>
          <h1>Penetration overview</h1>
        </div>
        <button type="button" className="pr2-primary">View opportunities →</button>
      </header>

      <FilterBar brandOptions={SEED_BRANDS} />
      <ComparisonBar />

      {loading ? (
        <div className="pr2-loading"><Spinner /></div>
      ) : (
        <div className="pr2-grid">
          <section className="pr2-card pr2-hero">
            <HeroCard metrics={heroMetrics} comparisonEnabled={comparisonEnabled} comparePeriod={comparePeriod} />
            <TrendChart trend={trend} comparisonEnabled={comparisonEnabled} />
          </section>
          <SignalTiles tiles={signalTiles} comparisonEnabled={comparisonEnabled} />
          <BreakdownTable rows={breakdownRows} comparisonEnabled={comparisonEnabled} />
          <FootprintMap records={filteredRecords} />
        </div>
      )}
    </div>
  );
}

export default PenetrationReportV2;
